import { useOrganization, useUser } from "@clerk/expo";
import { useRouter } from "expo-router";
import {
  Archive,
  Calendar,
  RotateCcw,
  Trash2,
  Users,
} from "lucide-react-native";
import { FC, useMemo, useState } from "react";

import {
  ActionMenu,
  Badge,
  BadgeText,
  Box,
  Card,
  DeleteDialog,
  HStack,
  Image,
  Pressable,
  Text,
  VStack,
} from "@/components/ui";
import {
  useDatetimeUtils,
  useLocalization,
  useOrganizationMembers,
} from "@/hooks";
import API from "@/hooks/api";
import { AdhocQuestion } from "@/types/resources";

interface QuestionCardProps {
  onDeleted?: () => void;
  question: AdhocQuestion;
}

const QuestionCard: FC<QuestionCardProps> = ({ onDeleted, question }) => {
  const { user } = useUser();
  const { membership, organization } = useOrganization();
  const { formatMemberName, users: membersMap } = useOrganizationMembers();
  const { i18n, browserLocale } = useLocalization();
  const { formatDate } = useDatetimeUtils();
  const router = useRouter();

  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);

  const {
    archiveQuestion,
    error: archiveError,
    loading: archiving,
  } = API.Adhoc.useArchiveQuestion();

  const {
    deleteQuestion,
    error: deleteError,
    loading: deleting,
  } = API.Adhoc.useDeleteQuestion();

  const isArchived = !!question.archived_at;
  const isAdmin = user?.publicMetadata?.admin === true;
  const canDelete =
    isAdmin || !organization || membership?.role === "org:admin";

  const tag = (() => {
    if (question.last_run && question.last_run.conclusion === null) {
      return { action: "muted" as const, text: i18n.t("misc.in_progress") };
    }
    if (question.last_run?.conclusion === "success") {
      return { action: "success" as const, text: i18n.t("misc.completed") };
    }
    if (question.last_run?.conclusion === "failed") {
      return { action: "error" as const, text: i18n.t("misc.failed") };
    }
    return { action: "warning" as const, text: i18n.t("misc.draft") };
  })();

  const menuActions = useMemo(() => {
    if (isArchived) {
      const actions = [
        ...(question.can_edit
          ? [
              {
                color: "typography" as const,
                icon: RotateCcw,
                label: i18n.t("misc.restore"),
                onPress: () => setShowRestoreDialog(true),
              },
            ]
          : []),
      ];
      return actions.length > 0 ? actions : null;
    }

    return [
      ...(question.can_edit
        ? [
            {
              color: "error" as const,
              divider: true as const,
              icon: Archive,
              label: i18n.t("misc.archive"),
              onPress: () => setShowArchiveDialog(true),
            },
          ]
        : []),
      ...(canDelete
        ? [
            {
              color: "error" as const,
              icon: Trash2,
              label: i18n.t("misc.deletePermanently"),
              onPress: () => setShowDeleteDialog(true),
            },
          ]
        : []),
    ];
  }, [canDelete, i18n, isArchived, question.can_edit]);

  const author = question.created_by_id
    ? membersMap[question.created_by_id]
    : undefined;

  return (
    <>
      <Pressable onPress={() => router.push(`/adhoc/questions/${question.id}`)}>
        <Card className="relative border border-outline-200 rounded-2xl px-5 py-4 hover:border-outline-300 hover:shadow-soft-2 hover:-translate-y-0.5 transition-all duration-300">
          {menuActions && menuActions.length > 0 && (
            <Box className="absolute top-3 right-3 z-10">
              <ActionMenu actions={menuActions} />
            </Box>
          )}
          <VStack className="flex-1 min-w-0 gap-1">
            <HStack className="items-center gap-2 pr-8">
              <Text
                className={`text-sm font-semibold line-clamp-1 flex-1 ${question.text ? "text-typography-900" : "text-typography-400 italic"}`}
              >
                {question.text || i18n.t("questions.untitled")}
              </Text>
              <Badge
                action={tag.action}
                className="px-2 py-0.5 rounded-full shrink-0"
                variant="solid"
              >
                <BadgeText className="text-2xs font-medium lowercase">
                  {tag.text}
                </BadgeText>
              </Badge>
            </HStack>
            <HStack className="items-center justify-between mt-2">
              <HStack className="items-center gap-3">
                {question.num_respondents > 0 && (
                  <HStack className="items-center gap-1">
                    <Users className="text-typography-400" size={12} />
                    <Text className="text-xs text-typography-500">
                      {i18n.numberToDelimited(question.num_respondents, {
                        delimiter: browserLocale.digitGroupingSeparator!,
                      })}
                    </Text>
                  </HStack>
                )}
                <HStack className="items-center gap-1">
                  <Calendar className="text-typography-400" size={12} />
                  <Text className="text-xs text-typography-500">
                    {formatDate(question.created_at)}
                  </Text>
                </HStack>
              </HStack>
              {author && (
                <HStack className="items-center gap-1.5">
                  <Image
                    alt={formatMemberName(author, true)}
                    className="w-4 h-4 rounded-full"
                    source={{ uri: author.imageUrl }}
                  />
                  <Text className="text-xs font-medium text-typography-500">
                    {formatMemberName(author, true)}
                  </Text>
                </HStack>
              )}
            </HStack>
          </VStack>
        </Card>
      </Pressable>

      <DeleteDialog
        body={i18n.t("questions.confirmArchiveMessage")}
        deleteButtonText={i18n.t("misc.archive")}
        error={archiveError}
        isOpen={showArchiveDialog}
        loading={archiving}
        loadingText={i18n.t("misc.archiving")}
        onCancel={() => setShowArchiveDialog(false)}
        onDelete={async () => {
          const result = await archiveQuestion(question.id, true);
          if (result) {
            setShowArchiveDialog(false);
            onDeleted?.();
          }
        }}
        title={i18n.t("questions.archiveQuestion")}
      />

      <DeleteDialog
        body={i18n.t("questions.confirmRestoreMessage")}
        deleteButtonText={i18n.t("misc.restore")}
        error={archiveError}
        isOpen={showRestoreDialog}
        loading={archiving}
        loadingText={i18n.t("misc.restoring")}
        onCancel={() => setShowRestoreDialog(false)}
        onDelete={async () => {
          const result = await archiveQuestion(question.id, false);
          if (result) {
            setShowRestoreDialog(false);
            onDeleted?.();
          }
        }}
        title={i18n.t("questions.restoreQuestion")}
      />

      <DeleteDialog
        body={i18n.t("questions.confirmDeletePermanentlyMessage")}
        deleteButtonText={i18n.t("misc.deletePermanently")}
        error={deleteError}
        isOpen={showDeleteDialog}
        loading={deleting}
        loadingText={i18n.t("misc.deleting")}
        onCancel={() => setShowDeleteDialog(false)}
        onDelete={async () => {
          const result = await deleteQuestion(question.id);
          if (result) {
            setShowDeleteDialog(false);
            onDeleted?.();
          }
        }}
        title={i18n.t("questions.deleteQuestionPermanently")}
      />
    </>
  );
};

export default QuestionCard;
